'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetProposalRow } from '@morfi/backend-sdk-v2/client';
import { useActiveWallet, useGetProposal, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { LiveProposalBackersPanel, ProposalBackersPanel } from '@/components/launchpad/proposal-backers-panel';
import { ProposalCreatorCard } from '@/components/launchpad/proposal-creator-card';
import { PROPOSAL_DETAIL_MAIN_COLUMN_CLASS, PROPOSAL_DETAIL_RIGHT_RAIL_CLASS, PROPOSAL_DETAIL_ROW_CLASS } from '@/components/launchpad/proposal-detail-layout-classes';
import { ProposalDetailRight } from '@/components/launchpad/proposal-detail-right';
import { ProposalDetailSkeleton } from '@/components/launchpad/proposal-detail-skeleton';
import { ProposalGraduationRedirect } from '@/components/launchpad/proposal-graduation-redirect';
import { ProposalHeader } from '@/components/launchpad/proposal-header';
import { ProposalHeroStake } from '@/components/launchpad/proposal-hero-stake';
import { ProposalOutcomesPanel } from '@/components/launchpad/proposal-outcomes-panel';
import type { ProposalScore } from '@/components/launchpad/proposal-score-badge';
import { ProposalSummaryCells } from '@/components/launchpad/proposal-summary-cells';
import { StickyProposalBar } from '@/components/launchpad/sticky-proposal-bar';
import { SuggestedOutcomesSection } from '@/components/launchpad/suggested-outcomes/SuggestedOutcomesSection';
import type { FixtureEntry } from '@/components/launchpad/use-proposal-fixtures';
import { EventTimeCard, type EventTimeCardProps } from '@/components/live/event-time-card';
import { MarketIdentitySection } from '@/components/market/identity/market-identity-section';
import { SimilarMarketsSection } from '@/components/markets/similar-markets';
import { DemoNotFoundCard } from '@/components/shared/demo-not-found-card';
import { Icon } from '@/components/ui/icon';
import { Search02Icon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { deriveBackerCountDisplay, deriveFakeBackers, deriveFakeDescription, deriveFakeThreshold, deriveFakeTotalFunded, deriveProposerProposalCount, deriveProposerTopMarketUsdcD6 } from '@/lib/launchpad/derive-fake-fields';
import { launchDisplayFundedD6 } from '@/lib/launchpad/proposal-launch';
import { PROPOSAL_DETAIL_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { useDemoMode } from '@/providers/demo-mode-provider';
import { useFixtureDetailStore } from '@/stores/fixture-detail';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ProposalDetailPageProps {
  proposalPubkey: string;
  /** Live-event association supplied by the event proposal query owner. */
  eventContext?: Omit<EventTimeCardProps, 'variant'>;
  /** Developer-playground input; the production route continues to resolve through the SDK. */
  fixture?: FixtureEntry;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ProposalDisplayStatus = 'active' | 'needs_debate' | 'pending_review' | 'creating' | 'live' | 'rejected' | 'expired';
interface DetailOutcome {
  index: number;
  label: string;
  imageUrl: string;
  imageNsfw: boolean;
  stakeAmount: string;
  percentage: number;
  backerCount: number;
}
interface DetailUserStakePosition {
  outcomeIndex: number;
  amount: string;
  tokenPrice: string | null;
  tokensReceived: string | null;
}
interface DetailUserStake {
  positions: DetailUserStakePosition[];
  totalAmount: string;
}

// ---------------------------------------------------------------------------
// Extracted layout sections (module scope — keeps ProposalDetailPage small)
// ---------------------------------------------------------------------------

interface FixtureProposalDetailBodyProps {
  proposalPubkey: string;
  fixtureProposal: {
    title: string;
    description: string | null;
    coverImageUrl: string | null;
    coverImageNsfw?: boolean;
    category: string;
    proposer: string;
    proposerDisplayName?: string | null;
    proposerHandle?: string | null;
    firstStakeAt?: string | null;
    debateSpreadOk: boolean;
    proposalScore?: ProposalScore;
    marketPubkey: string | null;
  };
  fixtureStatus: ProposalDisplayStatus;
  outcomes: DetailOutcome[];
  userStake: DetailUserStake | null;
  backers: BackerEntry[];
  displayTotalFunded: string;
  effectiveCreationThreshold: string | null;
  backerCountDisplay: number;
  activeWalletAddress: string | undefined;
  isAuthenticated: boolean;
  stickyOpen: boolean;
  onStickyOpenChange: (open: boolean) => void;
  suggestOutcomeModalOpen: boolean;
  onSuggestModalOpenChange: (open: boolean) => void;
  onConnectWallet: () => void;
  onMobileBackClick: () => void;
  eventContext: Omit<EventTimeCardProps, 'variant'> | undefined;
  showFixtureBanner: boolean;
}
function FixtureProposalDetailBody(t0) {
  const $ = _c(110);
  const {
    proposalPubkey,
    fixtureProposal: fp,
    fixtureStatus,
    outcomes,
    userStake,
    backers,
    displayTotalFunded,
    effectiveCreationThreshold,
    backerCountDisplay,
    activeWalletAddress,
    isAuthenticated,
    stickyOpen,
    onStickyOpenChange,
    suggestOutcomeModalOpen,
    onSuggestModalOpenChange,
    onConnectWallet,
    onMobileBackClick,
    eventContext,
    showFixtureBanner
  } = t0;
  let t1;
  if ($[0] !== showFixtureBanner) {
    t1 = showFixtureBanner ? <div className="mb-4 rounded-control border border-feedback-warning-border/30 bg-feedback-warning-accent/10 px-3 py-2 text-body text-feedback-warning-content">{"Fixture mode \u2014 showing dev fixture data"}</div> : null;
    $[0] = showFixtureBanner;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== displayTotalFunded || $[3] !== effectiveCreationThreshold || $[4] !== eventContext || $[5] !== fixtureStatus) {
    t2 = eventContext !== undefined ? <EventTimeCard variant={fixtureStatus === "expired" ? "didNotOpen" : "proposal"} {...eventContext} totalBackedD6={displayTotalFunded} {...effectiveCreationThreshold === null ? {} : {
      goalD6: effectiveCreationThreshold
    }} /> : null;
    $[2] = displayTotalFunded;
    $[3] = effectiveCreationThreshold;
    $[4] = eventContext;
    $[5] = fixtureStatus;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== fp.description || $[8] !== proposalPubkey) {
    t3 = fp.description !== null && fp.description.length > 0 ? fp.description : deriveFakeDescription(proposalPubkey);
    $[7] = fp.description;
    $[8] = proposalPubkey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = fp.coverImageNsfw === true;
  let t5;
  if ($[10] !== fp.category || $[11] !== fp.coverImageUrl || $[12] !== fp.title || $[13] !== proposalPubkey || $[14] !== t3 || $[15] !== t4) {
    t5 = <ProposalHeader proposal={{
      pubkey: proposalPubkey,
      title: fp.title,
      description: t3,
      coverImageUrl: fp.coverImageUrl,
      coverImageNsfw: t4,
      category: fp.category
    }} />;
    $[10] = fp.category;
    $[11] = fp.coverImageUrl;
    $[12] = fp.title;
    $[13] = proposalPubkey;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== backerCountDisplay || $[18] !== displayTotalFunded || $[19] !== effectiveCreationThreshold || $[20] !== fixtureStatus || $[21] !== proposalPubkey) {
    t6 = {
      pubkey: proposalPubkey,
      totalFunded: displayTotalFunded,
      creationThreshold: effectiveCreationThreshold,
      backerCount: backerCountDisplay,
      status: fixtureStatus
    };
    $[17] = backerCountDisplay;
    $[18] = displayTotalFunded;
    $[19] = effectiveCreationThreshold;
    $[20] = fixtureStatus;
    $[21] = proposalPubkey;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== backers) {
    t7 = backers.slice(0, 5).map(_temp);
    $[23] = backers;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== onMobileBackClick || $[26] !== t6 || $[27] !== t7) {
    t8 = <ProposalHeroStake proposal={t6} topBackers={t7} onMobileBackClick={onMobileBackClick} />;
    $[25] = onMobileBackClick;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t5 || $[30] !== t8) {
    t9 = <Panel elevation="elevated" className="flex min-w-0 flex-col gap-4 p-4" data-testid="proposal-detail-header-panel">{t5}{t8}</Panel>;
    $[29] = t5;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const t10 = activeWalletAddress === fp.proposer;
  const t11 = activeWalletAddress === undefined;
  let t12;
  if ($[32] !== fp.category || $[33] !== onConnectWallet || $[34] !== onSuggestModalOpenChange || $[35] !== outcomes || $[36] !== proposalPubkey || $[37] !== t10 || $[38] !== t11) {
    t12 = <ProposalOutcomesPanel proposalPubkey={proposalPubkey} proposalCategory={fp.category} outcomes={outcomes} onSuggestModalOpenChange={onSuggestModalOpenChange} isCreator={t10} isLoggedOut={t11} onRequireSignIn={onConnectWallet} />;
    $[32] = fp.category;
    $[33] = onConnectWallet;
    $[34] = onSuggestModalOpenChange;
    $[35] = outcomes;
    $[36] = proposalPubkey;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const t13 = fp.proposalScore ?? "good";
  let t14;
  if ($[40] !== t13) {
    t14 = <ProposalSummaryCells score={t13} className="@min-[1024px]/proposal-detail:hidden" />;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== backers) {
    t15 = <ProposalBackersPanel backers={backers} />;
    $[42] = backers;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const t16 = fp.proposerDisplayName ?? null;
  const t17 = fp.proposerHandle ?? null;
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [];
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== fp.proposer || $[46] !== t16 || $[47] !== t17) {
    t19 = {
      walletAddress: fp.proposer,
      displayName: t16,
      handle: t17,
      avatarUrl: null,
      avatarNsfw: false,
      connectedProviders: t18
    };
    $[45] = fp.proposer;
    $[46] = t16;
    $[47] = t17;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== fp.firstStakeAt) {
    t20 = fp.firstStakeAt ?? new Date().toISOString();
    $[49] = fp.firstStakeAt;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== fp.proposer) {
    t21 = deriveProposerTopMarketUsdcD6(fp.proposer);
    $[51] = fp.proposer;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== fp.proposer) {
    t22 = deriveProposerProposalCount(fp.proposer);
    $[53] = fp.proposer;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t19 || $[56] !== t20 || $[57] !== t21 || $[58] !== t22) {
    t23 = <ProposalCreatorCard creator={t19} createdAt={t20} topMarketUsdcD6={t21} proposalCount={t22} />;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const t24 = fp.marketPubkey ?? proposalPubkey;
  const t25 = fixtureStatus === "pending_review" ? 0 : 1;
  let t26;
  if ($[60] !== fp.proposer || $[61] !== fp.title || $[62] !== onConnectWallet || $[63] !== onSuggestModalOpenChange || $[64] !== suggestOutcomeModalOpen || $[65] !== t24 || $[66] !== t25) {
    t26 = <SuggestedOutcomesSection marketPubkey={t24} marketCreator={fp.proposer} marketStatus={t25} marketTitle={fp.title} onRequireSignIn={onConnectWallet} externalOpen={suggestOutcomeModalOpen} onExternalOpenChange={onSuggestModalOpenChange} />;
    $[60] = fp.proposer;
    $[61] = fp.title;
    $[62] = onConnectWallet;
    $[63] = onSuggestModalOpenChange;
    $[64] = suggestOutcomeModalOpen;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t12 || $[69] !== t14 || $[70] !== t15 || $[71] !== t2 || $[72] !== t23 || $[73] !== t26 || $[74] !== t9) {
    t27 = <div className={PROPOSAL_DETAIL_MAIN_COLUMN_CLASS}>{t2}{t9}{t12}{t14}{t15}{t23}{t26}</div>;
    $[68] = t12;
    $[69] = t14;
    $[70] = t15;
    $[71] = t2;
    $[72] = t23;
    $[73] = t26;
    $[74] = t9;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  const t28 = effectiveCreationThreshold ?? undefined;
  let t29;
  if ($[76] !== displayTotalFunded || $[77] !== fixtureStatus || $[78] !== fp.title || $[79] !== isAuthenticated || $[80] !== onConnectWallet || $[81] !== outcomes || $[82] !== proposalPubkey || $[83] !== t28 || $[84] !== userStake) {
    t29 = <ProposalDetailRight proposalPubkey={proposalPubkey} proposalTitle={fp.title} proposalStatus={fixtureStatus} outcomes={outcomes} userStake={userStake} isAuthenticated={isAuthenticated} withdrawFeePercent={0} feeBpsBacking={0} feeBpsUnbacking={0} currentTotalBackedD6={displayTotalFunded} creationThresholdD6={t28} onConnectWallet={onConnectWallet} />;
    $[76] = displayTotalFunded;
    $[77] = fixtureStatus;
    $[78] = fp.title;
    $[79] = isAuthenticated;
    $[80] = onConnectWallet;
    $[81] = outcomes;
    $[82] = proposalPubkey;
    $[83] = t28;
    $[84] = userStake;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  const t30 = fp.proposalScore ?? "good";
  let t31;
  if ($[86] !== t30) {
    t31 = <ProposalSummaryCells score={t30} />;
    $[86] = t30;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[88] !== t29 || $[89] !== t31) {
    t32 = <div className={PROPOSAL_DETAIL_RIGHT_RAIL_CLASS}>{t29}{t31}</div>;
    $[88] = t29;
    $[89] = t31;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== t27 || $[92] !== t32) {
    t33 = <div className={PROPOSAL_DETAIL_ROW_CLASS}>{t27}{t32}</div>;
    $[91] = t27;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  const t34 = effectiveCreationThreshold ?? undefined;
  let t35;
  if ($[94] !== displayTotalFunded || $[95] !== fixtureStatus || $[96] !== fp.title || $[97] !== isAuthenticated || $[98] !== onConnectWallet || $[99] !== onStickyOpenChange || $[100] !== outcomes || $[101] !== proposalPubkey || $[102] !== stickyOpen || $[103] !== t34 || $[104] !== userStake) {
    t35 = <StickyProposalBar proposalPubkey={proposalPubkey} proposalTitle={fp.title} proposalStatus={fixtureStatus} outcomes={outcomes} userStake={userStake} isAuthenticated={isAuthenticated} withdrawFeePercent={0} feeBpsBacking={0} feeBpsUnbacking={0} currentTotalBackedD6={displayTotalFunded} creationThresholdD6={t34} onConnectWallet={onConnectWallet} open={stickyOpen} onOpenChange={onStickyOpenChange} />;
    $[94] = displayTotalFunded;
    $[95] = fixtureStatus;
    $[96] = fp.title;
    $[97] = isAuthenticated;
    $[98] = onConnectWallet;
    $[99] = onStickyOpenChange;
    $[100] = outcomes;
    $[101] = proposalPubkey;
    $[102] = stickyOpen;
    $[103] = t34;
    $[104] = userStake;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  let t36;
  if ($[106] !== t1 || $[107] !== t33 || $[108] !== t35) {
    t36 = <div data-testid="proposal-detail-responsive-shell" className="@container/proposal-detail min-w-0 overflow-x-clip">{t1}{t33}{t35}</div>;
    $[106] = t1;
    $[107] = t33;
    $[108] = t35;
    $[109] = t36;
  } else {
    t36 = $[109];
  }
  return t36;
}
function _temp(b) {
  return {
    address: b.address,
    displayName: b.displayName,
    avatarUrl: b.avatarUrl,
    avatarNsfw: b.avatarNsfw
  };
}
interface LiveProposalDetailBodyProps {
  /** Live on chain: shown funded and launching while the market page prefetches. */
  launching: boolean;
  proposalPubkey: string;
  proposal: GetProposalRow;
  proposalStatus: ProposalDisplayStatus;
  outcomes: DetailOutcome[];
  userStake: DetailUserStake | null;
  backers: BackerEntry[];
  graduatedMarketPubkey: string | null;
  displayTotalFunded: string;
  effectiveCreationThreshold: string | null;
  backerCountDisplay: number;
  withdrawFeePercent: number | undefined;
  feeBpsBacking: number | undefined;
  feeBpsUnbacking: number | undefined;
  proposerWalletAddress: string;
  proposerDisplayName: string | null;
  proposerHandle: string | null;
  proposerAvatarUrl: string | null;
  activeWalletAddress: string | undefined;
  isAuthenticated: boolean;
  stickyOpen: boolean;
  onStickyOpenChange: (open: boolean) => void;
  suggestOutcomeModalOpen: boolean;
  onSuggestModalOpenChange: (open: boolean) => void;
  onConnectWallet: () => void;
  onMobileBackClick: () => void;
  eventContext: Omit<EventTimeCardProps, 'variant'> | undefined;
}
function LiveProposalDetailBody(t0) {
  const $ = _c(121);
  const {
    launching,
    proposalPubkey,
    proposal,
    proposalStatus,
    outcomes,
    userStake,
    backers,
    graduatedMarketPubkey,
    displayTotalFunded,
    effectiveCreationThreshold,
    backerCountDisplay,
    withdrawFeePercent,
    feeBpsBacking,
    feeBpsUnbacking,
    proposerWalletAddress,
    proposerDisplayName,
    proposerHandle,
    proposerAvatarUrl,
    activeWalletAddress,
    isAuthenticated,
    stickyOpen,
    onStickyOpenChange,
    suggestOutcomeModalOpen,
    onSuggestModalOpenChange,
    onConnectWallet,
    onMobileBackClick,
    eventContext
  } = t0;
  let t1;
  if ($[0] !== displayTotalFunded || $[1] !== effectiveCreationThreshold || $[2] !== eventContext || $[3] !== proposalStatus) {
    t1 = eventContext !== undefined ? <EventTimeCard variant={proposalStatus === "expired" ? "didNotOpen" : "proposal"} {...eventContext} totalBackedD6={displayTotalFunded} {...effectiveCreationThreshold === null ? {} : {
      goalD6: effectiveCreationThreshold
    }} /> : null;
    $[0] = displayTotalFunded;
    $[1] = effectiveCreationThreshold;
    $[2] = eventContext;
    $[3] = proposalStatus;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = proposal.coverImageUrl ?? null;
  const t3 = proposal.coverImageNsfw === true;
  let t4;
  if ($[5] !== proposal.category || $[6] !== proposal.description || $[7] !== proposal.marketType || $[8] !== proposal.title || $[9] !== proposalPubkey || $[10] !== t2 || $[11] !== t3) {
    t4 = <ProposalHeader proposal={{
      pubkey: proposalPubkey,
      title: proposal.title,
      description: proposal.description,
      coverImageUrl: t2,
      coverImageNsfw: t3,
      category: proposal.category,
      marketType: proposal.marketType
    }} />;
    $[5] = proposal.category;
    $[6] = proposal.description;
    $[7] = proposal.marketType;
    $[8] = proposal.title;
    $[9] = proposalPubkey;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== backerCountDisplay || $[14] !== displayTotalFunded || $[15] !== effectiveCreationThreshold || $[16] !== proposalPubkey || $[17] !== proposalStatus) {
    t5 = {
      pubkey: proposalPubkey,
      totalFunded: displayTotalFunded,
      creationThreshold: effectiveCreationThreshold,
      backerCount: backerCountDisplay,
      status: proposalStatus
    };
    $[13] = backerCountDisplay;
    $[14] = displayTotalFunded;
    $[15] = effectiveCreationThreshold;
    $[16] = proposalPubkey;
    $[17] = proposalStatus;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== backers) {
    t6 = backers.slice(0, 5).map(_temp2);
    $[19] = backers;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== launching || $[22] !== onMobileBackClick || $[23] !== t5 || $[24] !== t6) {
    t7 = <ProposalHeroStake launching={launching} proposal={t5} topBackers={t6} onMobileBackClick={onMobileBackClick} />;
    $[21] = launching;
    $[22] = onMobileBackClick;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t4 || $[27] !== t7) {
    t8 = <Panel className="flex min-w-0 flex-col gap-4 p-4" data-testid="proposal-detail-header-panel">{t4}{t7}</Panel>;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== proposal) {
    t9 = <MarketIdentitySection identity={proposal} />;
    $[29] = proposal;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const t10 = activeWalletAddress === proposerWalletAddress;
  const t11 = activeWalletAddress === undefined;
  let t12;
  if ($[31] !== onConnectWallet || $[32] !== onSuggestModalOpenChange || $[33] !== outcomes || $[34] !== proposal.category || $[35] !== proposalPubkey || $[36] !== t10 || $[37] !== t11) {
    t12 = <ProposalOutcomesPanel proposalPubkey={proposalPubkey} proposalCategory={proposal.category} outcomes={outcomes} onSuggestModalOpenChange={onSuggestModalOpenChange} isCreator={t10} isLoggedOut={t11} onRequireSignIn={onConnectWallet} />;
    $[31] = onConnectWallet;
    $[32] = onSuggestModalOpenChange;
    $[33] = outcomes;
    $[34] = proposal.category;
    $[35] = proposalPubkey;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const t13 = (proposal.proposalScore ?? "medium") as ProposalScore;
  let t14;
  if ($[39] !== t13) {
    t14 = <ProposalSummaryCells score={t13} className="@min-[1024px]/proposal-detail:hidden" />;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== proposalPubkey) {
    t15 = <LiveProposalBackersPanel proposalPubkey={proposalPubkey} />;
    $[41] = proposalPubkey;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const t16 = proposal.proposer.avatarNsfw ?? false;
  const t17 = proposal.proposer.avatarPreferences ?? null;
  let t18;
  if ($[43] !== proposal.proposer.connectedProviders) {
    t18 = proposal.proposer.connectedProviders ?? [];
    $[43] = proposal.proposer.connectedProviders;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== proposerAvatarUrl || $[46] !== proposerDisplayName || $[47] !== proposerHandle || $[48] !== proposerWalletAddress || $[49] !== t16 || $[50] !== t17 || $[51] !== t18) {
    t19 = {
      walletAddress: proposerWalletAddress,
      displayName: proposerDisplayName,
      handle: proposerHandle,
      avatarUrl: proposerAvatarUrl,
      avatarNsfw: t16,
      avatarPreferences: t17,
      connectedProviders: t18
    };
    $[45] = proposerAvatarUrl;
    $[46] = proposerDisplayName;
    $[47] = proposerHandle;
    $[48] = proposerWalletAddress;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  const t20 = proposal.marketType === "influencer";
  const t21 = proposal.associatedCreator?.partnerSocial ?? null;
  const t22 = proposal.proposer.topMarketVolume ?? "0";
  const t23 = proposal.proposer.proposalsCreated ?? 0;
  let t24;
  if ($[53] !== proposal.createdAt || $[54] !== t19 || $[55] !== t20 || $[56] !== t21 || $[57] !== t22 || $[58] !== t23) {
    t24 = <ProposalCreatorCard creator={t19} partner={t20} partnerSocial={t21} createdAt={proposal.createdAt} topMarketUsdcD6={t22} proposalCount={t23} />;
    $[53] = proposal.createdAt;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  const t25 = graduatedMarketPubkey ?? proposalPubkey;
  const t26 = proposalStatus === "pending_review" || proposalStatus === "creating" ? 0 : proposalStatus === "active" || proposalStatus === "needs_debate" ? 1 : 2;
  let t27;
  if ($[60] !== onConnectWallet || $[61] !== onSuggestModalOpenChange || $[62] !== proposal.proposer.walletAddress || $[63] !== proposal.title || $[64] !== suggestOutcomeModalOpen || $[65] !== t25 || $[66] !== t26) {
    t27 = <SuggestedOutcomesSection marketPubkey={t25} marketCreator={proposal.proposer.walletAddress} marketStatus={t26} marketTitle={proposal.title} onRequireSignIn={onConnectWallet} externalOpen={suggestOutcomeModalOpen} onExternalOpenChange={onSuggestModalOpenChange} />;
    $[60] = onConnectWallet;
    $[61] = onSuggestModalOpenChange;
    $[62] = proposal.proposer.walletAddress;
    $[63] = proposal.title;
    $[64] = suggestOutcomeModalOpen;
    $[65] = t25;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== proposalPubkey) {
    t28 = <SimilarMarketsSection marketPubkey={proposalPubkey} />;
    $[68] = proposalPubkey;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t1 || $[71] !== t12 || $[72] !== t14 || $[73] !== t15 || $[74] !== t24 || $[75] !== t27 || $[76] !== t28 || $[77] !== t8 || $[78] !== t9) {
    t29 = <div className={PROPOSAL_DETAIL_MAIN_COLUMN_CLASS}>{t1}{t8}{t9}{t12}{t14}{t15}{t24}{t27}{t28}</div>;
    $[70] = t1;
    $[71] = t12;
    $[72] = t14;
    $[73] = t15;
    $[74] = t24;
    $[75] = t27;
    $[76] = t28;
    $[77] = t8;
    $[78] = t9;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  const t30 = effectiveCreationThreshold ?? undefined;
  let t31;
  if ($[80] !== displayTotalFunded || $[81] !== feeBpsBacking || $[82] !== feeBpsUnbacking || $[83] !== isAuthenticated || $[84] !== launching || $[85] !== onConnectWallet || $[86] !== outcomes || $[87] !== proposal.title || $[88] !== proposalPubkey || $[89] !== proposalStatus || $[90] !== t30 || $[91] !== userStake || $[92] !== withdrawFeePercent) {
    t31 = <ProposalDetailRight launching={launching} proposalPubkey={proposalPubkey} proposalTitle={proposal.title} proposalStatus={proposalStatus} outcomes={outcomes} userStake={userStake} isAuthenticated={isAuthenticated} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} currentTotalBackedD6={displayTotalFunded} creationThresholdD6={t30} onConnectWallet={onConnectWallet} />;
    $[80] = displayTotalFunded;
    $[81] = feeBpsBacking;
    $[82] = feeBpsUnbacking;
    $[83] = isAuthenticated;
    $[84] = launching;
    $[85] = onConnectWallet;
    $[86] = outcomes;
    $[87] = proposal.title;
    $[88] = proposalPubkey;
    $[89] = proposalStatus;
    $[90] = t30;
    $[91] = userStake;
    $[92] = withdrawFeePercent;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  const t32 = (proposal.proposalScore ?? "medium") as ProposalScore;
  let t33;
  if ($[94] !== t32) {
    t33 = <ProposalSummaryCells score={t32} />;
    $[94] = t32;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== t31 || $[97] !== t33) {
    t34 = <div className={PROPOSAL_DETAIL_RIGHT_RAIL_CLASS}>{t31}{t33}</div>;
    $[96] = t31;
    $[97] = t33;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  let t35;
  if ($[99] !== t29 || $[100] !== t34) {
    t35 = <div className={PROPOSAL_DETAIL_ROW_CLASS}>{t29}{t34}</div>;
    $[99] = t29;
    $[100] = t34;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  const t36 = effectiveCreationThreshold ?? undefined;
  let t37;
  if ($[102] !== displayTotalFunded || $[103] !== feeBpsBacking || $[104] !== feeBpsUnbacking || $[105] !== isAuthenticated || $[106] !== launching || $[107] !== onConnectWallet || $[108] !== onStickyOpenChange || $[109] !== outcomes || $[110] !== proposal.title || $[111] !== proposalPubkey || $[112] !== proposalStatus || $[113] !== stickyOpen || $[114] !== t36 || $[115] !== userStake || $[116] !== withdrawFeePercent) {
    t37 = <StickyProposalBar launching={launching} proposalPubkey={proposalPubkey} proposalTitle={proposal.title} proposalStatus={proposalStatus} outcomes={outcomes} userStake={userStake} isAuthenticated={isAuthenticated} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} currentTotalBackedD6={displayTotalFunded} creationThresholdD6={t36} onConnectWallet={onConnectWallet} open={stickyOpen} onOpenChange={onStickyOpenChange} />;
    $[102] = displayTotalFunded;
    $[103] = feeBpsBacking;
    $[104] = feeBpsUnbacking;
    $[105] = isAuthenticated;
    $[106] = launching;
    $[107] = onConnectWallet;
    $[108] = onStickyOpenChange;
    $[109] = outcomes;
    $[110] = proposal.title;
    $[111] = proposalPubkey;
    $[112] = proposalStatus;
    $[113] = stickyOpen;
    $[114] = t36;
    $[115] = userStake;
    $[116] = withdrawFeePercent;
    $[117] = t37;
  } else {
    t37 = $[117];
  }
  let t38;
  if ($[118] !== t35 || $[119] !== t37) {
    t38 = <div data-testid="proposal-detail-responsive-shell" className="@container/proposal-detail min-w-0 overflow-x-clip">{t35}{t37}</div>;
    $[118] = t35;
    $[119] = t37;
    $[120] = t38;
  } else {
    t38 = $[120];
  }
  return t38;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function _temp2(b) {
  return {
    address: b.address,
    displayName: b.displayName,
    avatarUrl: b.avatarUrl,
    avatarNsfw: b.avatarNsfw
  };
}
export function ProposalDetailPage(t0) {
  const $ = _c(116);
  const {
    proposalPubkey,
    eventContext,
    fixture
  } = t0;
  const t = useTranslations("launchpad.detail");
  const {
    isAuthenticated,
    openLogin
  } = useAuthGate();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    isDemoMode
  } = useDemoMode();
  const [stickyOpen, setStickyOpen] = useState(false);
  const [suggestOutcomeModalOpen, setSuggestOutcomeModalOpen] = useState(false);
  const [watchedBacking, setWatchedBacking] = useState(false);
  const storedFixture = useFixtureDetailStore(_temp3);
  const isDev = process.env.NODE_ENV === "development";
  const explicitFixtureMatch = fixture !== undefined && fixture.proposal.pubkey === proposalPubkey ? fixture : null;
  const storedFixtureMatch = isDev && storedFixture !== null && storedFixture.proposal.pubkey === proposalPubkey ? storedFixture : null;
  const fixtureMatch = explicitFixtureMatch ?? storedFixtureMatch;
  let t1;
  if ($[0] !== activeWallet) {
    t1 = activeWallet?.address !== undefined ? {
      viewerWallet: activeWallet.address
    } : {};
    $[0] = activeWallet;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...PROPOSAL_DETAIL_FIELDS];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== proposalPubkey || $[4] !== t1) {
    t3 = {
      pubkey: proposalPubkey,
      ...t1,
      fields: t2
    };
    $[3] = proposalPubkey;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const proposalQuery = useGetProposal(t3);
  const proposal = proposalQuery.data;
  if (proposal !== undefined && proposal.status !== "live" && !watchedBacking) {
    setWatchedBacking(true);
  }
  const config = useMarketConfig(proposal?.marketConfig);
  const t4 = fixtureMatch !== null || proposal !== undefined;
  let t5;
  if ($[6] !== proposalPubkey || $[7] !== t4) {
    t5 = {
      name: "proposal_detail",
      isReady: t4,
      instanceKey: proposalPubkey
    };
    $[6] = proposalPubkey;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useRoutePerf(t5);
  const proposalStatusForRedirect = proposal?.status;
  const graduatedMarketForRedirect = proposal?.graduatedMarketPubkey;
  let t6;
  let t7;
  if ($[9] !== proposalPubkey) {
    t6 = () => {
      onEvent("proposal_detail_viewed", {
        proposalId: proposalPubkey
      });
    };
    t7 = [proposalPubkey];
    $[9] = proposalPubkey;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] !== fixtureMatch || $[13] !== graduatedMarketForRedirect || $[14] !== proposalPubkey || $[15] !== proposalStatusForRedirect) {
    t8 = () => {
      if (fixtureMatch !== null || proposalStatusForRedirect !== "live") {
        return;
      }
      if (graduatedMarketForRedirect !== null && graduatedMarketForRedirect !== undefined && graduatedMarketForRedirect.length > 0) {
        return;
      }
      logger.error("proposal.detail.graduated_market_missing", {
        proposalPubkey,
        status: proposalStatusForRedirect
      });
    };
    t9 = [fixtureMatch, graduatedMarketForRedirect, proposalPubkey, proposalStatusForRedirect];
    $[12] = fixtureMatch;
    $[13] = graduatedMarketForRedirect;
    $[14] = proposalPubkey;
    $[15] = proposalStatusForRedirect;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] !== openLogin) {
    t10 = function handleConnectWallet() {
      openLogin();
    };
    $[18] = openLogin;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleConnectWallet = t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = function handleMobileBackClick() {
      setStickyOpen(true);
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleMobileBackClick = t11;
  if (fixtureMatch !== null) {
    const fp = fixtureMatch.proposal;
    const fixtureStatus = fp.status;
    let fixtureOutcomes;
    let fixtureUserStake;
    let t12;
    if ($[21] !== fixtureMatch.userStake || $[22] !== fp.backerCount || $[23] !== fp.outcomes || $[24] !== proposalPubkey) {
      fixtureOutcomes = fp.outcomes.map(_temp4);
      let t13;
      if ($[28] !== fixtureMatch.userStake) {
        t13 = fixtureMatch.userStake !== null ? {
          positions: fixtureMatch.userStake.positions.map(_temp5),
          totalAmount: fixtureMatch.userStake.totalAmount
        } : null;
        $[28] = fixtureMatch.userStake;
        $[29] = t13;
      } else {
        t13 = $[29];
      }
      fixtureUserStake = t13;
      t12 = fp.backerCount === 0 ? [] : deriveFakeBackers(proposalPubkey, fixtureOutcomes.map(_temp6));
      $[21] = fixtureMatch.userStake;
      $[22] = fp.backerCount;
      $[23] = fp.outcomes;
      $[24] = proposalPubkey;
      $[25] = fixtureOutcomes;
      $[26] = fixtureUserStake;
      $[27] = t12;
    } else {
      fixtureOutcomes = $[25];
      fixtureUserStake = $[26];
      t12 = $[27];
    }
    const fixtureBackers = t12;
    let t13;
    if ($[30] !== fp.creationThreshold || $[31] !== proposalPubkey) {
      t13 = fp.creationThreshold !== "0" ? fp.creationThreshold : deriveFakeThreshold(proposalPubkey);
      $[30] = fp.creationThreshold;
      $[31] = proposalPubkey;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    const fixtureEffectiveCreationThreshold = t13;
    let t14;
    if ($[33] !== fixtureEffectiveCreationThreshold || $[34] !== fp.totalFunded || $[35] !== proposalPubkey) {
      t14 = fp.totalFunded === "0" ? "0" : deriveFakeTotalFunded(proposalPubkey, fp.totalFunded, fixtureEffectiveCreationThreshold);
      $[33] = fixtureEffectiveCreationThreshold;
      $[34] = fp.totalFunded;
      $[35] = proposalPubkey;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    const fixtureDisplayTotalFunded = t14;
    let t15;
    if ($[37] !== fp.backerCount || $[38] !== proposalPubkey) {
      t15 = fp.backerCount === 0 ? 0 : deriveBackerCountDisplay(proposalPubkey, fp.backerCount);
      $[37] = fp.backerCount;
      $[38] = proposalPubkey;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    const fixtureBackerCountDisplay = t15;
    const t16 = activeWallet?.address;
    const t17 = explicitFixtureMatch === null;
    let t18;
    if ($[40] !== eventContext || $[41] !== fixtureBackerCountDisplay || $[42] !== fixtureBackers || $[43] !== fixtureDisplayTotalFunded || $[44] !== fixtureEffectiveCreationThreshold || $[45] !== fixtureOutcomes || $[46] !== fixtureStatus || $[47] !== fixtureUserStake || $[48] !== fp || $[49] !== handleConnectWallet || $[50] !== isAuthenticated || $[51] !== proposalPubkey || $[52] !== stickyOpen || $[53] !== suggestOutcomeModalOpen || $[54] !== t16 || $[55] !== t17) {
      t18 = <FixtureProposalDetailBody proposalPubkey={proposalPubkey} fixtureProposal={fp} fixtureStatus={fixtureStatus} outcomes={fixtureOutcomes} userStake={fixtureUserStake} backers={fixtureBackers} displayTotalFunded={fixtureDisplayTotalFunded} effectiveCreationThreshold={fixtureEffectiveCreationThreshold} backerCountDisplay={fixtureBackerCountDisplay} activeWalletAddress={t16} isAuthenticated={isAuthenticated} stickyOpen={stickyOpen} onStickyOpenChange={setStickyOpen} suggestOutcomeModalOpen={suggestOutcomeModalOpen} onSuggestModalOpenChange={setSuggestOutcomeModalOpen} onConnectWallet={handleConnectWallet} onMobileBackClick={handleMobileBackClick} eventContext={eventContext} showFixtureBanner={t17} />;
      $[40] = eventContext;
      $[41] = fixtureBackerCountDisplay;
      $[42] = fixtureBackers;
      $[43] = fixtureDisplayTotalFunded;
      $[44] = fixtureEffectiveCreationThreshold;
      $[45] = fixtureOutcomes;
      $[46] = fixtureStatus;
      $[47] = fixtureUserStake;
      $[48] = fp;
      $[49] = handleConnectWallet;
      $[50] = isAuthenticated;
      $[51] = proposalPubkey;
      $[52] = stickyOpen;
      $[53] = suggestOutcomeModalOpen;
      $[54] = t16;
      $[55] = t17;
      $[56] = t18;
    } else {
      t18 = $[56];
    }
    return t18;
  }
  if (proposalQuery.isPending) {
    let t12;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <ProposalDetailSkeleton />;
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    return t12;
  }
  if (proposal === undefined) {
    if (isDemoMode) {
      let t12;
      if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = <DemoNotFoundCard kind="proposal" />;
        $[58] = t12;
      } else {
        t12 = $[58];
      }
      return t12;
    }
    let t12;
    if ($[59] !== t) {
      t12 = t("notFoundIcon");
      $[59] = t;
      $[60] = t12;
    } else {
      t12 = $[60];
    }
    let t13;
    if ($[61] !== t12) {
      t13 = <Icon icon={Search02Icon} className="size-12 text-foreground-secondary" aria-label={t12} />;
      $[61] = t12;
      $[62] = t13;
    } else {
      t13 = $[62];
    }
    let t14;
    if ($[63] !== t) {
      t14 = t("notFound");
      $[63] = t;
      $[64] = t14;
    } else {
      t14 = $[64];
    }
    let t15;
    if ($[65] !== t14) {
      t15 = <h2 className="text-heading">{t14}</h2>;
      $[65] = t14;
      $[66] = t15;
    } else {
      t15 = $[66];
    }
    let t16;
    if ($[67] !== t) {
      t16 = t("notFoundDescription");
      $[67] = t;
      $[68] = t16;
    } else {
      t16 = $[68];
    }
    let t17;
    if ($[69] !== t16) {
      t17 = <p className="text-body text-foreground-secondary">{t16}</p>;
      $[69] = t16;
      $[70] = t17;
    } else {
      t17 = $[70];
    }
    let t18;
    if ($[71] !== t13 || $[72] !== t15 || $[73] !== t17) {
      t18 = <div><div className="mx-auto flex flex-col items-center gap-2 py-16 text-center">{t13}{t15}{t17}</div></div>;
      $[71] = t13;
      $[72] = t15;
      $[73] = t17;
      $[74] = t18;
    } else {
      t18 = $[74];
    }
    return t18;
  }
  const launching = proposal.status === "live";
  let t12;
  if ($[75] !== activeWallet?.address || $[76] !== launching || $[77] !== proposal || $[78] !== watchedBacking) {
    t12 = launching ? <ProposalGraduationRedirect proposal={proposal} walletAddress={activeWallet?.address} watchedGoLive={watchedBacking} /> : null;
    $[75] = activeWallet?.address;
    $[76] = launching;
    $[77] = proposal;
    $[78] = watchedBacking;
    $[79] = t12;
  } else {
    t12 = $[79];
  }
  const launchHandoff = t12;
  if (launching && (proposal.graduatedMarketPubkey ?? "") === "") {
    return launchHandoff;
  }
  let t13;
  if ($[80] !== proposal) {
    t13 = mapLiveProposalRows(proposal);
    $[80] = proposal;
    $[81] = t13;
  } else {
    t13 = $[81];
  }
  const {
    outcomes,
    backers
  } = t13;
  const viewerShareBalanceD6 = proposal.viewerShareBalance;
  let t14;
  if ($[82] !== viewerShareBalanceD6) {
    t14 = typeof viewerShareBalanceD6 === "string" && viewerShareBalanceD6.length > 0 ? {
      positions: [],
      totalAmount: viewerShareBalanceD6
    } : null;
    $[82] = viewerShareBalanceD6;
    $[83] = t14;
  } else {
    t14 = $[83];
  }
  const userStake = t14;
  const proposalStatus = (launching ? "active" : proposal.status) as ProposalDisplayStatus;
  const graduatedMarketPubkey = proposal.graduatedMarketPubkey ?? null;
  const withdrawFeePercent = proposalStatus === "rejected" || proposalStatus === "expired" ? 0 : config === undefined ? undefined : config.feeBpsUnbacking / 100;
  const feeBpsBacking = config?.feeBpsBacking;
  const feeBpsUnbacking = config?.feeBpsUnbacking;
  const proposerWalletAddress = proposal.proposer.walletAddress;
  const proposerDisplayName = proposal.proposer.displayName;
  const proposerHandle = proposal.proposer.handle;
  const proposerAvatarUrl = proposal.proposer.avatarUrl;
  const effectiveCreationThreshold = config?.creationThresholdD6 ?? null;
  let t15;
  if ($[84] !== effectiveCreationThreshold || $[85] !== launching || $[86] !== proposal.totalFunded) {
    t15 = launching ? launchDisplayFundedD6(proposal.totalFunded, effectiveCreationThreshold) : proposal.totalFunded;
    $[84] = effectiveCreationThreshold;
    $[85] = launching;
    $[86] = proposal.totalFunded;
    $[87] = t15;
  } else {
    t15 = $[87];
  }
  const displayTotalFunded = t15;
  const backerCountDisplay = proposal.numBackers;
  const t16 = activeWallet?.address;
  let t17;
  if ($[88] !== backerCountDisplay || $[89] !== backers || $[90] !== displayTotalFunded || $[91] !== effectiveCreationThreshold || $[92] !== eventContext || $[93] !== feeBpsBacking || $[94] !== feeBpsUnbacking || $[95] !== graduatedMarketPubkey || $[96] !== handleConnectWallet || $[97] !== isAuthenticated || $[98] !== launching || $[99] !== outcomes || $[100] !== proposal || $[101] !== proposalPubkey || $[102] !== proposalStatus || $[103] !== proposerAvatarUrl || $[104] !== proposerDisplayName || $[105] !== proposerHandle || $[106] !== proposerWalletAddress || $[107] !== stickyOpen || $[108] !== suggestOutcomeModalOpen || $[109] !== t16 || $[110] !== userStake || $[111] !== withdrawFeePercent) {
    t17 = <LiveProposalDetailBody launching={launching} proposalPubkey={proposalPubkey} proposal={proposal} proposalStatus={proposalStatus} outcomes={outcomes} userStake={userStake} backers={backers} graduatedMarketPubkey={graduatedMarketPubkey} displayTotalFunded={displayTotalFunded} effectiveCreationThreshold={effectiveCreationThreshold} backerCountDisplay={backerCountDisplay} withdrawFeePercent={withdrawFeePercent} feeBpsBacking={feeBpsBacking} feeBpsUnbacking={feeBpsUnbacking} proposerWalletAddress={proposerWalletAddress} proposerDisplayName={proposerDisplayName} proposerHandle={proposerHandle} proposerAvatarUrl={proposerAvatarUrl} activeWalletAddress={t16} isAuthenticated={isAuthenticated} stickyOpen={stickyOpen} onStickyOpenChange={setStickyOpen} suggestOutcomeModalOpen={suggestOutcomeModalOpen} onSuggestModalOpenChange={setSuggestOutcomeModalOpen} onConnectWallet={handleConnectWallet} onMobileBackClick={handleMobileBackClick} eventContext={eventContext} />;
    $[88] = backerCountDisplay;
    $[89] = backers;
    $[90] = displayTotalFunded;
    $[91] = effectiveCreationThreshold;
    $[92] = eventContext;
    $[93] = feeBpsBacking;
    $[94] = feeBpsUnbacking;
    $[95] = graduatedMarketPubkey;
    $[96] = handleConnectWallet;
    $[97] = isAuthenticated;
    $[98] = launching;
    $[99] = outcomes;
    $[100] = proposal;
    $[101] = proposalPubkey;
    $[102] = proposalStatus;
    $[103] = proposerAvatarUrl;
    $[104] = proposerDisplayName;
    $[105] = proposerHandle;
    $[106] = proposerWalletAddress;
    $[107] = stickyOpen;
    $[108] = suggestOutcomeModalOpen;
    $[109] = t16;
    $[110] = userStake;
    $[111] = withdrawFeePercent;
    $[112] = t17;
  } else {
    t17 = $[112];
  }
  let t18;
  if ($[113] !== launchHandoff || $[114] !== t17) {
    t18 = <>{launchHandoff}{t17}</>;
    $[113] = launchHandoff;
    $[114] = t17;
    $[115] = t18;
  } else {
    t18 = $[115];
  }
  return t18;
}

/** Map the proposal response's outcomes and embedded recent backers to the body's rows. */
function _temp6(o_0) {
  return {
    index: o_0.index,
    label: o_0.label
  };
}
function _temp5(p) {
  return {
    outcomeIndex: p.outcomeIndex,
    amount: p.amount,
    tokenPrice: null as string | null,
    tokensReceived: null as string | null
  };
}
function _temp4(o) {
  return {
    index: o.index,
    label: o.label,
    imageUrl: o.imageUrl ?? "",
    imageNsfw: o.imageNsfw === true,
    stakeAmount: o.stakeAmount,
    percentage: o.percentage,
    backerCount: o.backerCount
  };
}
function _temp3(s) {
  return s.entry;
}
function mapLiveProposalRows(proposal: GetProposalRow): {
  outcomes: DetailOutcome[];
  backers: BackerEntry[];
} {
  // ── Map API outcomes to component shape ─────────────────────────────────
  const backerRows = Array.isArray(proposal.backers) ? proposal.backers : [];
  const backerCountByOutcome = new Map<number, number>();
  for (const b of backerRows) {
    const prev = backerCountByOutcome.get(b.outcomeIndex) ?? 0;
    backerCountByOutcome.set(b.outcomeIndex, prev + 1);
  }
  const outcomes: Array<{
    index: number;
    label: string;
    imageUrl: string;
    imageNsfw: boolean;
    stakeAmount: string;
    percentage: number;
    backerCount: number;
  }> = Array.isArray(proposal.outcomes) ? proposal.outcomes.map(o => ({
    index: o.outcomeIndex,
    label: o.label,
    imageUrl: o.imageUrl ?? '',
    imageNsfw: o.imageNsfw === true,
    stakeAmount: o.funded,
    percentage: Number.parseFloat(o.fundedPercent),
    backerCount: backerCountByOutcome.get(o.outcomeIndex) ?? 0
  })) : [];

  // ── Map backers from API to component shape ─────────────────────────────
  // The proposal response embeds `backers` (recent backers, server-capped)
  // via the v2 `?fields=backers` projection — no separate hook needed.
  const backers: BackerEntry[] = backerRows.map(b => ({
    address: b.backer.walletAddress,
    displayName: b.backer.displayName,
    handle: b.backer.handle,
    avatarUrl: b.backer.avatarUrl,
    avatarNsfw: b.backer.avatarNsfw ?? false,
    // ALLOW-RASTER-LITERAL: object-default for missing avatarPreferences; the literal
    // `false` matches the FQ-RASTER-003 opt-in default (no user preference → no rasterise).
    avatarPreferences: b.backer.avatarPreferences ?? {
      rasterise: false
    },
    outcomeIndex: b.outcomeIndex,
    outcomeLabel: outcomes.find(o => o.index === b.outcomeIndex)?.label ?? `Outcome ${String(b.outcomeIndex)}`,
    amount: b.amount,
    timestamp: b.backedAt
  }));
  return {
    outcomes,
    backers
  };
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface BackerEntry {
  address: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  avatarPreferences?: {
    rasterise: boolean;
  };
  outcomeIndex: number;
  outcomeLabel: string;
  amount: string;
  timestamp: string;
}